import React, { useEffect, useMemo, useState } from 'react';
import { DatePicker } from 'antd';
import dayjs from 'dayjs';

import css from './OrdersSComponent.module.css';
import { statisticsService } from '../../../services/statistics-services';
import { formatDateToString } from '../../../helpers/formatDateToString';
import { PromoCodeUsageChart, generateColor } from './PromoCodeUsageChart';
import { ClientRegistrationsChart } from './ClientRegistrationsChart';

const getDefaultRange = () => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - 6); // last 7 days inclusive
    return { start, end };
};

const OrdersSComponent = () => {
    const defaultRange = useMemo(getDefaultRange, []);
    const [startDate, setStartDate] = useState(defaultRange.start);
    const [endDate, setEndDate] = useState(defaultRange.end);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [usageData, setUsageData] = useState([]);
    const [totals, setTotals] = useState({});
    const [clientUsage, setClientUsage] = useState([]);
    const [clientTotal, setClientTotal] = useState(0);

    const normalizeUsage = (raw) => {
        const rawArray = Array.isArray(raw) ? raw : (Array.isArray(raw?.usage) ? raw.usage : []);
        // Build complete date list inclusive
        const result = [];
        const cursor = new Date(startDate);
        const endCopy = new Date(endDate);
        while (cursor <= endCopy) {
            const d = formatDateToString(cursor);
            const found = rawArray.find((x) => x?.date === d);
            result.push({ date: d, counts: found?.counts || {} });
            cursor.setDate(cursor.getDate() + 1);
        }
        return result;
    };

    const fetchData = async () => {
        try {
            setIsLoading(true);
            setError('');
            const [promo, clients] = await Promise.all([
                statisticsService.getPromoCodeUsageByDateRange({
                    startDate: formatDateToString(startDate),
                    endDate: formatDateToString(endDate),
                }),
                statisticsService.getClientRegistrationsByDateRange({
                    startDate: formatDateToString(startDate),
                    endDate: formatDateToString(endDate),
                }),
            ]);
            setUsageData(normalizeUsage(promo));
            setTotals(promo?.total || {});

            const normalizeClientUsage = (raw) => {
                const rawArray = Array.isArray(raw?.usage) ? raw.usage : (Array.isArray(raw) ? raw : []);
                const dateToCount = new Map(rawArray.map((x) => [x?.date, typeof x?.counts === 'number' ? x.counts : 0]));
                const result = [];
                // Fix: reset times to midnight to ensure inclusive days
                const cursor = new Date(startDate);
                const endCopy = new Date(endDate);
                cursor.setHours(0, 0, 0, 0);
                endCopy.setHours(0, 0, 0, 0);
                while (cursor.getTime() <= endCopy.getTime()) {
                    const d = formatDateToString(cursor);
                    result.push({ date: d, counts: dateToCount.get(d) ?? 0 });
                    cursor.setDate(cursor.getDate() + 1);
                }
                return result;
            };

            const clientsNormalized = normalizeClientUsage(clients);
            setClientUsage(clientsNormalized);
            setClientTotal(clientsNormalized.reduce((sum, i) => sum + (i?.counts || 0), 0));
        } catch (e) {
            setError(e?.response?.data?.message || e?.message || 'Failed to load data');
            setUsageData([]);
            setTotals({});
            setClientUsage([]);
            setClientTotal(0);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onApplyRange = () => {
        if (!startDate || !endDate) return;
        if (startDate > endDate) {
            setError('Start date cannot be after end date');
            return;
        }
        fetchData();
    };

    return (
        <section className={css.wrap}>
            <div className={css.controlsCard}>
                <div className={css.controlGroup}>
                    <span className={css.label}>From</span>
                    <DatePicker 
                        value={startDate ? dayjs(startDate) : null} 
                        onChange={(date) => setStartDate(date ? date.toDate() : null)} 
                        format="YYYY-MM-DD" 
                        maxDate={dayjs()} 
                        className={css.datePicker} 
                    />
                </div>
                <div className={css.controlGroup}>
                    <span className={css.label}>To</span>
                    <DatePicker 
                        value={endDate ? dayjs(endDate) : null} 
                        onChange={(date) => setEndDate(date ? date.toDate() : null)} 
                        format="YYYY-MM-DD" 
                        maxDate={dayjs()} 
                        className={css.datePicker} 
                    />
                </div>
                <button type="button" onClick={onApplyRange} className={css.applyBtn}>Apply</button>
            </div>

            {isLoading && <div className={css.noData}>Loading...</div>}
            {error && !isLoading && <div className={css.noData} style={{ color: 'red' }}>{error}</div>}

            {!isLoading && !error && (
                <>
                    <div className={css.chartCard}>
                        {usageData?.length ? (
                            <div className={css.chartContainer}>
                                <PromoCodeUsageChart data={usageData} />
                            </div>
                        ) : (
                            <div className={css.noData}>No data</div>
                        )}
                        {!!Object.keys(totals || {}).length && (
                            <div className={css.totalsRow}>
                                {Object.entries(totals).map(([code, value]) => (
                                    <div key={code} className={css.pill}>
                                        <span className={css.dot} style={{ background: generateColor(code) }} />
                                        <span>{code}</span>
                                        <span style={{ fontWeight: 600 }}>{value}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className={css.chartCard} style={{ marginTop: 16 }}>
                        {clientUsage?.length ? (
                            <div className={css.chartContainer}>
                                <ClientRegistrationsChart data={clientUsage} />
                            </div>
                        ) : (
                            <div className={css.noData}>No data</div>
                        )}
                        <div className={css.totalsRow}>
                            <div className={css.pill}>
                                <span>Total</span>
                                <span style={{ fontWeight: 600 }}>{clientTotal}</span>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </section>
    );
};

export { OrdersSComponent };